<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { page } from "$app/stores";
  import { leftSidebarIsOpenStore } from "$lib/stores";
  import ScanIcon from "$lib/icons/ScanIcon.svelte";
  import { environment } from "$lib/env";

  export let data;
  let container: HTMLDivElement;
  let gameFrame: HTMLIFrameElement;
  let width = 0;
  let height = 0;
  let aspectRatio = 16 / 9;
  let isLoading = true;
  let countdownValue = 5;
  let countdownInterval;

  const apiUrl = environment.apiUrl;

  function openFullScreen() {
    if (gameFrame) {
      if (gameFrame.requestFullscreen) {
        gameFrame.requestFullscreen();
      } else if (gameFrame.webkitRequestFullscreen) {
        gameFrame.webkitRequestFullscreen();
      } else if (gameFrame.msRequestFullscreen) {
        gameFrame.msRequestFullscreen();
      }
    }
  }

  function updateDimensions() {
    if (container) {
      // Calculate container dimensions
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Always use full width of container
      width = containerWidth;

      // For mobile devices, use a different approach
      if (window.innerWidth <= 468) {
        height = window.screen.height - 180;
      } else {
        // For desktop, calculate height based on container width
        height = containerHeight;
      }

      // Update iframe dimensions if it exists
      if (gameFrame) {
        // Set the iframe to 100% width to fill the container
        gameFrame.style.width = '100%';
        gameFrame.style.height = `${height}px`;

        // Remove the absolute positioning that was causing centering
        gameFrame.style.position = 'relative';
        gameFrame.style.left = 'auto';
        gameFrame.style.top = 'auto';
        gameFrame.style.transform = 'none';
      }
    }
  }

  function redirectToHome() {
    if (countdownInterval) {
      clearInterval(countdownInterval);
    }
    window.location.href = '/';
  }

  function startCountdown() {
    countdownValue = 5;
    countdownInterval = setInterval(() => {
      countdownValue--;
      if (countdownValue <= 0) {
        clearInterval(countdownInterval);
        redirectToHome();
      }
    }, 1000);
  }

  async function fetchGameUrl() {
    try {
      const slug = $page.params.slug;
      const isDemo = $page.url.searchParams.get("demo") || "false";

      // Set up headers
      const headers = data.token
              ? { "Content-Type": "application/json", Authorization: `Bearer ${data.token}` }
              : { "Content-Type": "application/json" };

      const response = await fetch(`${apiUrl}/games/${slug}/launch?demo=${isDemo}`, {
        method: "POST",
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch game URL: ${response.status}`);
      }

      const gameData = await response.json();

      if (!gameData.data?.url) {
        throw new Error("No game URL found in response");
      }

      return gameData.data.url;
    } catch (error) {
      throw error;
    }
  }

  function createGameIframe(url) {
    if (!container) {
      isLoading = false;
      return;
    }

    // Clear any existing content
    container.innerHTML = '';

    // Create the iframe
    gameFrame = document.createElement('iframe');

    // Set to fill the container width
    gameFrame.style.width = '100%';
    gameFrame.style.border = 'none';
    gameFrame.style.background = '#000';
    gameFrame.title = data.game.gameName || 'Game';
    gameFrame.allow = "fullscreen";
    gameFrame.setAttribute("allowfullscreen", "true");

    // Use relative positioning instead of absolute
    gameFrame.style.position = 'relative';
    gameFrame.style.display = 'block';

    // Add load event listener to hide loading indicator
    gameFrame.addEventListener('load', () => {
      isLoading = false;
    });

    // Set the src to the game URL
    gameFrame.src = url;

    // Add the iframe to the container
    container.appendChild(gameFrame);

    // Update dimensions
    updateDimensions();
  }

  // Update the onMount function to call updateDimensions when the window is resized
  onMount(async () => {
    // Initial dimensions update
    updateDimensions();

    // Add resize listener
    const handleResize = () => {
      updateDimensions();
    };

    window.addEventListener('resize', handleResize);

    // Check if the game is restricted
    if (data.game.restricted) {
      isLoading = false;
      startCountdown();
    } else {
      // Game is not restricted, fetch the URL client-side
      try {
        const url = await fetchGameUrl();
        createGameIframe(url);
      } catch (error) {
        isLoading = false;
      }
    }

    // Set a timeout to hide the loading indicator after 10 seconds
    // in case the load event doesn't fire
    setTimeout(() => {
      if (isLoading) {
        isLoading = false;
      }
    }, 10000);

    leftSidebarIsOpenStore.subscribe(() => {
      updateDimensions();
    });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  onDestroy(() => {
    if (countdownInterval) {
      clearInterval(countdownInterval);
    }
  });
</script>

<svelte:head>
  <title>Forza.Bet - {data.game?.gameName || 'Game'}</title>
</svelte:head>

<div class="w-full bg-cosmic-dark-800 dark:bg-gray-800 rounded-lg overflow-hidden text-white">
  <div class="w-full relative">
    <div bind:this={container} class="iframe-container">
      {#if isLoading}
        <div class="loading-overlay">
          <div class="spinner"></div>
          <p>Loading game...</p>
        </div>
      {/if}

      {#if data.game.restricted}
        <div class="restriction-overlay">
          <div class="restriction-content">
            <div class="logo">
              <svg width="80" height="80" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 10C27.909 10 10 27.909 10 50C10 72.091 27.909 90 50 90C72.091 90 90 72.091 90 50C90 27.909 72.091 10 50 10Z" fill="#e53935"/>
                <path d="M65 35L45 65L35 50" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h2>SORRY</h2>
            <p>Wagering for this game is not legal from your jurisdiction.</p>
            <p>For further assistance, please contact support .</p>

            <button class="redirect-button" on:click={redirectToHome}>
              Back to lobby
            </button>

            <div class="countdown">
              You will be automatically redirected to the lobby in: <span class="timer">{countdownValue}</span>
            </div>
          </div>
        </div>
      {/if}
    </div>

    <div class="flex justify-between items-center p-4 bg-gray-800">
      <div class="text-sm md:text-md">{data.game?.gameName || 'Game'}</div>
      {#if !data.game?.restricted}
        <button on:click={openFullScreen} class="focus:outline-none">
          <ScanIcon />
        </button>
      {/if}
    </div>
  </div>
</div>

<style>
  .iframe-container {
    width: 100%;
    max-width: 100vw;
    overflow: hidden;
    position: relative;
    height: 80vh;
    min-height: 500px;
    background: rgb(31 41 55 / var(--tw-bg-opacity, 1));
    display: block;
  }

  iframe {
    display: block;
    background: rgb(31 41 55 / var(--tw-bg-opacity, 1));
    border: none;
    width: 100% !important;
    height: 100%;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgb(31 41 55 / var(--tw-bg-opacity, 1));
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
  }

  .restriction-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url("/bg-1.PNG");
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    background-position: center;
    background-size: cover;
  }

  .restriction-content {
    text-align: center;
    color: white;
    padding: 2rem;
    max-width: 500px;
  }

  .logo {
    margin: 0 auto 1.5rem;
    width: 80px;
    height: 80px;
  }

  h2 {
    font-size: 2.5rem;
    margin: 0 0 1.5rem 0;
    font-weight: 700;
  }

  p {
    margin: 0 0 1rem 0;
    font-size: 1.1rem;
    opacity: 0.9;
  }

  .redirect-button {
    margin: 1.5rem auto;
    padding: 0.75rem 2.5rem;
    background: #e53935;
    color: white;
    border: none;
    border-radius: 2rem;
    cursor: pointer;
    font-size: 1rem;
    transition: background-color 0.2s;
    display: block;
  }

  .redirect-button:hover {
    background: #c62828;
  }

  .countdown {
    margin-top: 1.5rem;
    font-size: 0.9rem;
    opacity: 0.7;
  }

  .timer {
    color: #e53935;
    font-weight: bold;
  }

  .spinner {
    width: 50px;
    height: 50px;
    border: 5px solid rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    border-top-color: #e53935;
    animation: spin 1s ease-in-out infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
</style>

